import type { ClickerCommand } from 'features/script/clicker'
import type { MediaProgress } from 'room/common/media-progress'
import type { MediaStatus } from 'room/common/model/media-status'

/**
 * What presenter mode says over the replication link (`replication/`), on
 * top of the state it carries, and straight between the two windows with
 * `window.postMessage`. Everything arriving is `unknown` until a guard below
 * says otherwise.
 */

export type ProjectorDisplay = 'windowed' | 'fullscreen'

/** What a key press on the projector asks the console to do. */
export type PresenterCommand = ClickerCommand | 'black'

/** Console to projector. */
export type ConsoleMessage =
	| { kind: 'end' }
	/** The recording on the stage failed to load; load it again. */
	| { kind: 'reload-media' }

/** Projector to console. */
export type ProjectorMessage =
	| { kind: 'display'; display: ProjectorDisplay; screenLabel?: string }
	| { kind: 'media'; media: MediaProgress | null }
	| { kind: 'command'; command: PresenterCommand }
	/** The recordings cannot play through the chosen speakers (or can again). */
	| { kind: 'speakers'; fellBack: boolean }

/**
 * Sent with `window.postMessage(…, { delegate: 'fullscreen' })` straight to
 * the presentation window: a `BroadcastChannel` cannot carry a delegated
 * capability.
 */
export type GoFullscreenMessage = {
	kind: 'go-fullscreen'
	presentation: string
}

/**
 * Sent by the presentation window straight to `window.opener` with every
 * snapshot: a console that reloaded learns from `event.source` which window to
 * delegate full screen to again.
 */
export type ProjectorHereMessage = {
	kind: 'projector-here'
	presentation: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value)

const isNumber = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value)

const isMediaStatus = (value: unknown): value is MediaStatus =>
	value === 'loading' || value === 'ready' || value === 'failed'

const isMediaProgress = (value: unknown): value is MediaProgress =>
	isRecord(value) &&
	((value.kind === 'youtube' && typeof value.cued === 'boolean') ||
		(value.kind === 'audio' && isMediaStatus(value.status))) &&
	isNumber(value.positionMs) &&
	isNumber(value.durationMs) &&
	typeof value.playing === 'boolean' &&
	isNumber(value.at)

const isCommand = (value: unknown): value is PresenterCommand =>
	value === 'next' || value === 'prev' || value === 'black'

export const readConsoleMessage = (
	payload: unknown
): ConsoleMessage | undefined => {
	if (!isRecord(payload)) {
		return undefined
	}
	switch (payload.kind) {
		case 'end':
			return { kind: 'end' }
		case 'reload-media':
			return { kind: 'reload-media' }
		default:
			return undefined
	}
}

export const readProjectorMessage = (
	payload: unknown
): ProjectorMessage | undefined => {
	if (!isRecord(payload)) {
		return undefined
	}
	switch (payload.kind) {
		case 'display':
			return (payload.display === 'windowed' ||
				payload.display === 'fullscreen') &&
				(payload.screenLabel === undefined ||
					typeof payload.screenLabel === 'string')
				? (payload as ProjectorMessage)
				: undefined
		case 'media':
			return payload.media === null || isMediaProgress(payload.media)
				? (payload as ProjectorMessage)
				: undefined
		case 'command':
			return isCommand(payload.command)
				? { kind: 'command', command: payload.command }
				: undefined
		case 'speakers':
			return typeof payload.fellBack === 'boolean'
				? { kind: 'speakers', fellBack: payload.fellBack }
				: undefined
		default:
			return undefined
	}
}

export const isGoFullscreen = (
	data: unknown,
	presentation: string
): data is GoFullscreenMessage =>
	isRecord(data) &&
	data.kind === 'go-fullscreen' &&
	data.presentation === presentation

export const isProjectorHere = (
	data: unknown,
	presentation: string
): data is ProjectorHereMessage =>
	isRecord(data) &&
	data.kind === 'projector-here' &&
	data.presentation === presentation
